import { memo } from 'react'

import { DegenHours } from '@root/features/services/wallet-profile'

import {
  analysisOf,
  curveOf,
  entryCapBuckets,
  holdFor,
  returnBuckets,
  signTone,
  winRateOf,
} from '../../model/portfolio-readings'

import { count, native, percent, signedUsd, usd } from './money'

import type { Tone } from '../../model/portfolio-readings'
import type { PortfolioUnit } from '../../model/portfolio-view-atom'
import type { WalletProfile } from '@root/features/services/wallet-profile'

/** A tone becomes a class here, and a colour only in the stylesheet. */
const toneClass = (tone: Tone) => {
  if (tone === 'good') {
    return 'good'
  }
  return tone === 'bad' ? 'bad' : undefined
}

/** The line and its wash take the colour of where the curve ended up. */
const strokeFor = (tone: Tone) => {
  if (tone === 'good') {
    return 'var(--up)'
  }
  return tone === 'bad' ? 'var(--down)' : 'var(--t4)'
}

/** Which of the two ramps a band is painted with. */
const bandColour = (key: string, tone: Tone) =>
  tone === 'bad' ? BAD[key] : GOOD[key]

/**
 * What the account is worth right now.
 *
 * Three readings that answer different questions and are routinely confused:
 * what the wallets hold at this second, what is riding on open positions, and
 * what could be spent without selling anything. Axiom puts the same three
 * together and it is the right grouping.
 */
export const BalancePanel: React.FC<{
  totalUsd: number | undefined
  totalNative: number | undefined
  unrealised: number | undefined
  spendableNative: number | undefined
  tokens: number | undefined
  wallets: number
  unit: PortfolioUnit
}> = memo(
  ({
    totalUsd,
    totalNative,
    unrealised,
    spendableNative,
    tokens,
    wallets,
    unit,
  }) => (
    <section className="pn balance">
      <header>
        <h2>Balance</h2>
      </header>
      <div className="in">
        <div className="big">
          <span className="fig">
            {unit === 'usd' ? usd(totalUsd) : native(totalNative)}
          </span>
          <span className="sub">
            {unit === 'usd'
              ? `${native(totalNative)} SOL`
              : `${usd(totalUsd)} at today's price`}
          </span>
        </div>

        <div className="tiles">
          <div className="tl">
            <i>Unrealised</i>
            <b className={toneClass(signTone(unrealised))}>
              {signedUsd(unrealised)}
            </b>
            <span>on what is still open</span>
          </div>
          <div className="tl">
            <i>Tradeable</i>
            <b>{native(spendableNative)}</b>
            <span>SOL, not in a position</span>
          </div>
          <div className="tl">
            <i>Tokens held</i>
            <b>{count(tokens)}</b>
            <span>with a balance</span>
          </div>
          <div className="tl">
            <i>Across</i>
            <b>{wallets}</b>
            <span>{wallets === 1 ? 'wallet' : 'wallets'}</span>
          </div>
        </div>
      </div>
    </section>
  ),
)

/**
 * What somebody else's wallet is worth, from the only thing we can read of it.
 *
 * We hold no balance for an address that is not ours, so this says what the
 * trading read says: the open positions at the price they trade at now, and
 * what is riding on them. It does not pretend to be a balance — a wallet also
 * holds coins it never traded, and nothing here can see those.
 */
export const GuestBalancePanel: React.FC<{
  profile: WalletProfile.WalletProfile | undefined
}> = memo(({ profile }) => {
  const open = (profile?.positions ?? []).filter(
    (position) => position.remainingTokens > 0,
  )
  const value = open.reduce(
    (total, position) => total + position.remainingTokens * position.priceUsd,
    0,
  )

  return (
    <section className="pn balance">
      <header>
        <h2>Open positions</h2>
      </header>
      <div className="in">
        <div className="big">
          <span className="fig">{usd(value)}</span>
          <span className="sub">at the price they trade at now</span>
        </div>

        <div className="tiles">
          <div className="tl">
            <i>Unrealised</i>
            <b className={toneClass(signTone(profile?.totals.unrealizedPnl))}>
              {signedUsd(profile?.totals.unrealizedPnl)}
            </b>
            <span>riding on them</span>
          </div>
          <div className="tl">
            <i>Still open</i>
            <b>{count(open.length)}</b>
            <span>of {count(profile?.totals.tokensTraded)} traded</span>
          </div>
        </div>

        <p className="hint">
          This is somebody else&apos;s wallet, so what is shown is what its
          trading says. A balance would need the wallet itself, and coins it
          never traded are not in this.
        </p>
      </div>
    </section>
  )
})

/**
 * The realised curve, and the period it covers.
 *
 * The period selector lives on this panel rather than over the whole screen,
 * because this is the only thing it changes: the totals and the positions come
 * from a per-wallet aggregate with no time dimension in it, so a period over
 * the page would silently claim to cut figures it cannot reach.
 */
export const CurvePanel: React.FC<{
  series: ReadonlyArray<WalletProfile.PnlPoint>
  realised: number | undefined
  unrealised: number | undefined
  total: number | undefined
  children?: React.ReactNode
}> = memo(({ series, realised, unrealised, total, children }) => {
  const points = series.map((point) => ({
    day: point.day,
    value: point.realizedPnl,
  }))
  const curve = curveOf(points, 600, 190, 6)
  const stroke = strokeFor(signTone(curve.last))

  return (
    <section className="pn curve">
      <header>
        <h2>Realised profit</h2>
        <div className="right">{children}</div>
      </header>
      <div className="in">
        <div className="tiles three">
          <div className="tl">
            <i>Realised</i>
            <b className={toneClass(signTone(realised))}>
              {signedUsd(realised)}
            </b>
          </div>
          <div className="tl">
            <i>Unrealised</i>
            <b className={toneClass(signTone(unrealised))}>
              {signedUsd(unrealised)}
            </b>
          </div>
          <div className="tl">
            <i>Together</i>
            <b className={toneClass(signTone(total))}>{signedUsd(total)}</b>
          </div>
        </div>

        <div className="plot">
          {curve.line ? (
            <svg viewBox="0 0 600 190" preserveAspectRatio="none">
              <defs>
                <linearGradient id="pf-wash" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={stroke} stopOpacity="0.26" />
                  <stop offset="100%" stopColor={stroke} stopOpacity="0" />
                </linearGradient>
              </defs>
              <line
                className="zero"
                x1="0"
                x2="600"
                y1={curve.zeroY}
                y2={curve.zeroY}
              />
              <path className="ar" d={curve.area} fill="url(#pf-wash)" />
              <path className="ln" d={curve.line} stroke={stroke} />
            </svg>
          ) : (
            <p className="empty">
              Nothing closed in this window yet. The line is drawn from realised
              profit, so a position still open does not move it.
            </p>
          )}
        </div>

        {points.length > 1 ? (
          <div className="days">
            <span>{points.at(0)?.day}</span>
            <span>{points.at(-1)?.day}</span>
          </div>
        ) : undefined}
      </div>
    </section>
  )
})

/**
 * How the trading went, not how much is in the account.
 *
 * Ours and theirs together: the counts, the win rate and the average hold are
 * figures our dashboard already computed, and the five return bands are the
 * ones Axiom shows. Sharpe carries a note, because a number nobody can name
 * is decoration.
 */
export const PerformancePanel: React.FC<{
  totals: WalletProfile.Totals | undefined
  distribution: WalletProfile.Distribution | undefined
}> = memo(({ totals, distribution }) => {
  const rate = totals
    ? winRateOf(totals.closedPositions, totals.winningClosedPositions)
    : undefined
  const buckets = distribution ? returnBuckets(distribution) : []
  const anyClosed = buckets.some((bucket) => bucket.count > 0)

  return (
    <section className="pn perf">
      <header>
        <h2>Performance</h2>
      </header>
      <div className="in">
        <div className="tiles">
          <div className="tl">
            <i>Win rate</i>
            <b>{percent(rate)}</b>
            <span>
              {totals ? `${count(totals.closedPositions)} closed` : '—'}
            </span>
          </div>
          <div className="tl">
            <i>Transactions</i>
            <b>{count(totals?.txes)}</b>
            <span>
              <span className="good">{count(totals?.buys)} buy</span>
              {' · '}
              <span className="bad">{count(totals?.sells)} sell</span>
            </span>
          </div>
          <div className="tl">
            <i>Avg hold</i>
            <b>{holdFor(totals?.avgHoldSeconds)}</b>
            <span>per closed position</span>
          </div>
          <div className="tl">
            <i title="Return per unit of its own swing. Above 1 is steady, below 0 is losing.">
              Sharpe
            </i>
            <b className={toneClass(signTone(totals?.sharpe ?? undefined))}>
              {totals?.sharpe === null || totals?.sharpe === undefined
                ? '—'
                : totals.sharpe.toFixed(2)}
            </b>
            <span>steadiness, not size</span>
          </div>
        </div>

        {anyClosed ? (
          <>
            <div className="stack">
              {buckets.map((bucket) =>
                bucket.share > 0 ? (
                  <span
                    key={bucket.key}
                    style={{
                      width: `${bucket.share}%`,
                      background: bandColour(bucket.key, bucket.tone),
                    }}
                  />
                ) : undefined,
              )}
            </div>

            <div className="bk">
              {[...buckets].reverse().map((bucket) => (
                <div className="rw" key={bucket.key}>
                  <span className="nm">{bucket.label}</span>
                  <span className="tr">
                    <span
                      className="fl"
                      style={{
                        width: `${bucket.share}%`,
                        background: bandColour(bucket.key, bucket.tone),
                      }}
                    />
                  </span>
                  <span className="ct">{bucket.count}</span>
                </div>
              ))}
            </div>
          </>
        ) : (
          <p className="blank">
            Nothing has closed yet, so there is no distribution to show. It
            fills in as positions are sold.
          </p>
        )}
      </div>
    </section>
  )
})

/* Two ramps rather than one colour each side, so the bands are told apart
   inside the stacked bar as well as beside their own labels. */
const GOOD: Record<string, string> = {
  gain0To200: 'rgba(70,184,125,.55)',
  gain200To500: 'rgba(70,184,125,.78)',
  gainOver500: '#46b87d',
}
const BAD: Record<string, string> = {
  loss0To50: 'rgba(225,92,92,.55)',
  lossOver50: '#e15c5c',
}

/**
 * What size of token this account buys into — ours, and nobody else shows it.
 *
 * The unknown band is named rather than dropped. It is usually the largest
 * one, because the entry cap is only known where the indexer saw the buy, and
 * hiding that would make three small numbers look like the whole story.
 */
export const EntryPanel: React.FC<{
  caps: WalletProfile.EntryMarketCaps | undefined
  funding: WalletProfile.Funding | undefined
}> = memo(({ caps, funding }) => {
  const buckets = caps ? entryCapBuckets(caps) : []
  const known = buckets
    .filter((bucket) => bucket.key !== 'unknown')
    .reduce((sum, bucket) => sum + bucket.count, 0)

  return (
    <section className="pn entry">
      <header>
        <h2>Entry market cap</h2>
      </header>
      <div className="in">
        {known > 0 ? (
          <div className="bk">
            {buckets
              .filter((bucket) => bucket.key !== 'unknown')
              .map((bucket) => (
                <div className="rw" key={bucket.key}>
                  <span className="nm">{bucket.label}</span>
                  <span className="tr">
                    <span
                      className="fl"
                      style={{
                        width: `${bucket.share}%`,
                        background: 'var(--accent)',
                      }}
                    />
                  </span>
                  <span className="ct">{bucket.count}</span>
                </div>
              ))}
          </div>
        ) : (
          <p className="blank">
            No entry is known yet. A cap is only recorded where the buy itself
            was seen.
          </p>
        )}

        <p className="hint">
          {known > 0
            ? `Of ${known} ${known === 1 ? 'entry' : 'entries'} we can place. ${
                caps?.unknown ?? 0
              } more were bought before the range this reads, or arrived by transfer.`
            : 'Tokens that arrived by transfer have no entry of their own.'}
        </p>

        {funding ? (
          <div className="tiles">
            <div className="tl">
              <i>First funded with</i>
              <b>{usd(funding.amount)}</b>
              <span>
                {funding.looksOriginal ? 'looks original' : 'forwarded on'}
              </span>
            </div>
            <div className="tl">
              <i>Funded</i>
              <b>{new Date(funding.at).toLocaleDateString()}</b>
              <span>first money in</span>
            </div>
          </div>
        ) : undefined}
      </div>
    </section>
  )
})

/**
 * What the money did, as our own dashboard has always put it.
 *
 * These are the figures the profile screen showed and the borrowed layout has
 * no place for — what was paid in, what a position costs and comes back as,
 * what is still riding. Kept because they answer a different question from the
 * profit ones: those say how it went, these say at what size.
 */
export const AnalysisPanel: React.FC<{
  totals: WalletProfile.Totals | undefined
  positions: ReadonlyArray<WalletProfile.Position> | undefined
}> = memo(({ totals, positions }) => {
  const reading = totals ? analysisOf(totals, positions ?? []) : undefined

  return (
    <section className="pn analysis">
      <header>
        <h2>Analysis</h2>
        <div className="right">
          <span className="tag">{count(totals?.tokensTraded)} tokens</span>
        </div>
      </header>
      <div className="in">
        <div className="tiles">
          <div className="tl">
            <i>Volume</i>
            <b>{usd(reading?.volumeUsd)}</b>
            <span>bought and sold</span>
          </div>
          <div className="tl">
            <i>Cost</i>
            <b>{usd(reading?.costUsd)}</b>
            <span>paid in, all told</span>
          </div>
          <div className="tl">
            <i>Avg cost / sold</i>
            <b>
              {usd(reading?.avgCostUsd)}
              <i
                style={{
                  fontStyle: 'normal',
                  color: 'var(--t4)',
                  fontSize: 12,
                }}
              >
                {' / '}
                {usd(reading?.avgSoldUsd)}
              </i>
            </b>
            <span>per token traded</span>
          </div>
          <div className="tl">
            <i>Avg realised</i>
            <b className={toneClass(signTone(reading?.avgRealisedUsd))}>
              {reading?.avgRealisedUsd === undefined
                ? '—'
                : signedUsd(reading.avgRealisedUsd)}
            </b>
            <span>per closed position</span>
          </div>
          <div className="tl">
            <i>Still held</i>
            <b>{usd(reading?.openValueUsd)}</b>
            <span>at today&apos;s price</span>
          </div>
          <div className="tl">
            <i>Unpriced</i>
            <b>{count(totals?.unpricedPositions)}</b>
            <span>no price to value them</span>
          </div>
        </div>

        <p className="hint">
          Fees are not among these. A pool&apos;s swap fee is inside the price
          each trade went off at and is never recorded apart from it, so the
          only figure we could show would be a nought meaning not known, rather
          than none paid.
        </p>
      </div>
    </section>
  )
})

const DAY_NAMES = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']

/**
 * The week, by the hour — ours, and the best thing on this page.
 *
 * Axiom shows a day-by-part-of-day block. This is all hundred and sixty-eight
 * hours, coloured by what one trade in that hour made on average rather than
 * by how many there were, which is the difference between a heatmap of edge
 * and a heatmap of when somebody was awake.
 */
export const HoursPanel: React.FC<{
  hours: DegenHours.DegenHours | undefined
  walletName: string | undefined
  loading: boolean
}> = memo(({ hours, walletName, loading }) => {
  const cells = hours?.cells ?? []
  const grid = hours ? DegenHours.byHour(hours) : new Map()
  const extreme = DegenHours.extremeOf(cells)
  const totals = DegenHours.totalsOf(cells)
  const best = DegenHours.bestHour(cells)

  return (
    <section className="pn hours">
      <header>
        <h2>Degen hours</h2>
        <div className="right">
          {walletName ? <span className="tag">{walletName}</span> : undefined}
          {hours ? <span className="tag">{hours.days}d</span> : undefined}
        </div>
      </header>
      <div className="in">
        {cells.length === 0 ? (
          <p className="blank">
            {loading
              ? 'Reading the last thirty days…'
              : 'No trades in the last thirty days, so there is no week to colour.'}
          </p>
        ) : (
          <>
            <div className="heat">
              <div className="rows">
                {DAY_NAMES.map((day) => (
                  <span key={day}>{day}</span>
                ))}
              </div>
              <div className="cells">
                {DegenHours.DAYS.map((day) => (
                  <div className="wk" key={day}>
                    {DegenHours.HOURS.map((hour) => {
                      const cell = DegenHours.cellAt(grid, day, hour)
                      const perTrade = cell ? DegenHours.perTrade(cell) : 0
                      const strength = cell
                        ? DegenHours.intensity(perTrade, extreme)
                        : 0
                      const colour =
                        perTrade >= 0
                          ? `rgba(70,184,125,${(0.12 + strength * 0.88).toFixed(3)})`
                          : `rgba(225,92,92,${(0.12 + strength * 0.88).toFixed(3)})`

                      return (
                        <span
                          className="cl"
                          key={hour}
                          data-on={cell ? '1' : '0'}
                          style={cell ? { background: colour } : undefined}
                          title={
                            cell
                              ? `${DAY_NAMES[day - 1]} ${String(hour).padStart(2, '0')}:00 · ${cell.trades} ${cell.trades === 1 ? 'trade' : 'trades'} · ${signedUsd(cell.pnl)} · ${percent(DegenHours.winRate(cell) * 100)} won`
                              : undefined
                          }
                        />
                      )
                    })}
                  </div>
                ))}
              </div>
              <span />
              <div className="marks">
                {DegenHours.HOURS.map((hour) => (
                  <span key={hour}>{hour % 6 === 0 ? hour : ''}</span>
                ))}
              </div>
            </div>

            <div className="hoursfoot">
              <span>
                {count(totals.trades)} trades ·{' '}
                <b className={toneClass(signTone(totals.perTrade))}>
                  {signedUsd(totals.perTrade)}
                </b>{' '}
                a trade
              </span>
              {best ? (
                <span>
                  Best hour {DAY_NAMES[best.day - 1]}{' '}
                  {String(best.hour).padStart(2, '0')}:00 ·{' '}
                  <b className={toneClass(signTone(best.pnl))}>
                    {signedUsd(best.pnl)}
                  </b>
                </span>
              ) : undefined}
              <span className="scale">
                loss
                <i style={{ background: '#e15c5c' }} />
                <i style={{ background: 'var(--chip)' }} />
                <i style={{ background: '#46b87d' }} />
                gain
              </span>
            </div>

            <p className="hint">
              Coloured by what one trade in that hour made on average, in your
              own timezone ({hours?.timezone}). An hour with no trades stays
              blank rather than becoming a zero.
            </p>
          </>
        )}
      </div>
    </section>
  )
})
